import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postCpaStatus } from '../../../data-layer/cpa/service';
import { CpaStatus } from '../../../data-layer/cpa/types';
import { showToastAsync } from '../../../data-layer/notifications/actions';
import * as partnerService from '../../../data-layer/partner/service';
import {
    FnsStatusEnum,
    PartnerRegistrationErrorEnum,
    PartnerType,
} from '../../../data-layer/partner/types';
import { useDispatch } from '../../../data-layer/useDispatch';
import {
    toBankDetailsDto,
    toSignerDataDto,
} from '../../../mappers/partner/toPartnerRegistrationDto';
import { PartnerApprovalPage } from '../../../ui/business/PartnerApprovalPage/PartnerApprovalPage';
import { PartnerBankDetails } from '../../../ui/business/PartnerBankDetails/PartnerBankDetails';
import { PartnerDownloadOffer } from '../../../ui/business/PartnerDownloadOffer/PartnerDownloadOffer';
import { PartnerRegistrationForm } from '../../../ui/business/PartnerRegistrationForm/PartnerRegistrationForm';
import { hasFieldErrors } from '../../../ui/business/PartnerRegistrationForm/fieldMapping';
import {
    PartnerBankDetailsUI,
    PartnerRegistrationUI,
    PartnerSignerDataUI,
} from '../../../ui/business/PartnerRegistrationForm/types';
import { PartnerSelfEmployedDialog } from '../../../ui/business/PartnerSelfEmployedDialog/PartnerSelfEmployedDialog';
import { PartnerSignerDataPage } from '../../../ui/business/PartnerSignerDataPage/PartnerSignerDataPage';
import { MOBILE_FOOTER_HEIGHT, MOBILE_HEADER_HEIGHT } from '../../../ui/dashboard/DashboardLayout';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { IconInfo } from '../../../ui/icon/IconInfo';
import { Flex } from '../../../ui/layout/Flex';
import { contentActionsService } from '../../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../../utils/LocationContext';
import { useCurrentUser } from '../../users/useCurrentUser';
import { withAuthProtection } from '../../withAuthProtection';
import { useRegisterStateMachine } from './hooks/useRegisterStateMachine';
import { ApiError, ErrorField, PartnerRegistrationSteps as Steps } from './types';
import {
    parseApprovalError,
    parseFieldError,
    parseGenericError,
    parseRegistrationError,
} from './utils/parseApiError';
import { registerPartner } from './utils/registerPartner';

export const PartnerRegistration = withAuthProtection(() => {
    const currentUser = useCurrentUser();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectUrl = useRef(location.state?.redirectUrl || '/profile');
    const { partnerType, step, onNextStep, onPartnerTypeChange, onStepChange, isInitialized } =
        useRegisterStateMachine();
    const { eventSource } = useEventSource();
    const [showSelfEmployedDialog, setShowSelfEmployedDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | ErrorField[]>();
    const [registrationFormData, setRegistrationFormData] = useState<PartnerRegistrationUI>();

    const dispatch = useDispatch();

    useEffect(() => {
        if (eventSource === 'partner_registration') {
            contentActionsService.trackPartnerRegistrationPageOpen({
                screen: eventSource,
            });
        }
    }, [eventSource]);

    useEffect(() => {
        setErrorMessage(undefined);
    }, [partnerType, step]);

    const logClick = useCallback(() => {
        if (eventSource) {
            contentActionsService.trackPartnerRegistrationNextClick({
                step,
                screen: eventSource,
            });
        }
    }, [eventSource, step]);

    const onRegisterClick = useCallback(
        async (formValues: PartnerRegistrationUI) => {
            if (isLoading) {
                return;
            }
            logClick();
            setIsLoading(true);
            setErrorMessage(undefined);

            try {
                const response = await registerPartner({ partnerType, formValues });
                setRegistrationFormData(formValues);

                if (partnerType === PartnerType.SE && response.fnsStatus === FnsStatusEnum.BOUND) {
                    onStepChange(Steps.BankData);
                } else {
                    onNextStep();
                }
            } catch (error) {
                const errorMsg = (error as ApiError).message;

                if (
                    partnerType === PartnerType.SE &&
                    errorMsg === PartnerRegistrationErrorEnum.UNREGISTERED
                ) {
                    setShowSelfEmployedDialog(true);
                } else {
                    const fallbackMessage =
                        partnerType === PartnerType.SE
                            ? 'Мы не смогли определить ИНН по введенным данным. Проверьте их корректность'
                            : 'Некорректный ОГРН/ОГРНИП/ИНН. Проверьте правильность введенных данных';

                    const registerErrorMessage = parseRegistrationError(error, fallbackMessage);
                    setErrorMessage(registerErrorMessage);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [logClick, onNextStep, onStepChange, partnerType, isLoading],
    );

    const onPartnerConfirmClick = useCallback(async () => {
        if (isLoading) {
            return;
        }
        logClick();
        setIsLoading(true);
        setErrorMessage(undefined);

        try {
            await partnerService.postSignupExternalSelfEmployed();
            const { data } = await partnerService.getSignupExternalSelfEmployed();

            if (data.fns_status === FnsStatusEnum.BOUND) {
                onNextStep();
            } else {
                throw new Error(PartnerRegistrationErrorEnum.NOT_BOUND);
            }
        } catch (error) {
            const confirmErrorMessage = parseApprovalError(
                error,
                'Вы не подтвердили права в Кабинет налогоплательщика. Если вы случайно отклонили форму в «Моём налоге» отправьте форму заново.',
            );
            setErrorMessage(confirmErrorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [logClick, onNextStep, isLoading]);

    const onResendFormData = useCallback(async () => {
        if (isLoading || !registrationFormData) {
            return;
        }
        logClick();
        setIsLoading(true);
        setErrorMessage(undefined);

        try {
            await registerPartner({ partnerType, formValues: registrationFormData });
            await partnerService.postSignupExternalSelfEmployed();
            const { data } = await partnerService.getSignupExternalSelfEmployed();

            if (data.fns_status === FnsStatusEnum.BOUND) {
                onNextStep();
            } else {
                throw new Error(PartnerRegistrationErrorEnum.NOT_BOUND);
            }
        } catch (error) {
            const resendErrorMessage = parseApprovalError(
                error,
                'Вы не подтвердили права в Кабинет налогоплательщика. Если вы случайно отклонили форму в «Моём налоге» отправьте форму заново.',
            );
            setErrorMessage(resendErrorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [logClick, onNextStep, isLoading, registrationFormData, partnerType]);

    const onBackFromApproval = useCallback(() => {
        onStepChange(Steps.Register);
    }, [onStepChange]);

    const handleSelfEmployedRegistered = useCallback(() => {
        setShowSelfEmployedDialog(false);
        onNextStep();
    }, [onNextStep]);

    const onBankDetailsSubmit = useCallback(
        async (formValues: PartnerBankDetailsUI) => {
            if (isLoading) {
                return;
            }
            logClick();
            setIsLoading(true);
            setErrorMessage(undefined);

            try {
                const bankInfo = await partnerService.getBankInfoByBic(formValues.bik);
                const dto = toBankDetailsDto(formValues, bankInfo.data.correspondent_account || '');
                await partnerService.postSignupExternalBank(dto);
                onNextStep();
            } catch (error) {
                const bankErrorMessage = parseFieldError(
                    error,
                    'account_nr',
                    'Ошибка сохранения банковских данных. Проверьте данные и попробуйте ещё раз',
                );
                setErrorMessage(bankErrorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [logClick, onNextStep, isLoading],
    );

    const onSignerDataSubmit = useCallback(
        async (formValues: PartnerSignerDataUI) => {
            if (isLoading) {
                return;
            }
            logClick();
            setIsLoading(true);
            setErrorMessage(undefined);

            try {
                const dto = toSignerDataDto(formValues);
                await partnerService.postSignupExternalSigner(dto);
                onNextStep();
            } catch (error) {
                const signerErrorMessage = parseFieldError(
                    error,
                    'full_name',
                    'Ошибка сохранения данных подписанта. Проверьте данные и попробуйте ещё раз',
                );
                setErrorMessage(signerErrorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [logClick, onNextStep, isLoading],
    );

    const onSubmitOfferClick = useCallback(async () => {
        if (isLoading) {
            return;
        }
        if (eventSource) {
            contentActionsService.trackPartnerRegistrationSubmit({
                step,
                screen: eventSource,
            });
        }
        setIsLoading(true);
        setErrorMessage(undefined);

        try {
            await partnerService.submitSignupExternal();
            await postCpaStatus({ status: CpaStatus.MODERATION });
            dispatch(
                showToastAsync({
                    message: 'Регистрация завершена',
                    subtitle: 'Оферта успешно отправлена',
                    icon: IconInfo,
                    color: 'invert',
                }),
            );
            navigate(redirectUrl.current);
        } catch (error) {
            const submitErrorMessage = parseGenericError(
                error,
                'Не удалось отправить оферту. Попробуйте ещё раз',
            );
            setErrorMessage(submitErrorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, eventSource, step, dispatch, navigate]);

    const onDownloadOfferError = useCallback((error: string) => {
        setErrorMessage(error);
    }, []);

    if (!currentUser || !isInitialized) {
        return null;
    }

    const fieldErrors =
        Array.isArray(errorMessage) && hasFieldErrors(errorMessage) ? errorMessage : undefined;

    const generalError =
        typeof errorMessage === 'string'
            ? errorMessage
            : Array.isArray(errorMessage) && !hasFieldErrors(errorMessage)
              ? 'Произошла ошибка при обработке данных'
              : undefined;

    return (
        <DashboardPage>
            <Flex
                my={{ xs: 0, sm: 2 }}
                px={2}
                pt={2}
                width="100%"
                minHeight={{
                    xs: `calc(100dvh - ${MOBILE_HEADER_HEIGHT}px - ${MOBILE_FOOTER_HEIGHT}px)`,
                    sm: 'unset',
                }}
                borderRadius={2}
                backgroundColor="var(--background-primary)"
            >
                {step === Steps.Register && (
                    <PartnerRegistrationForm
                        onSubmit={onRegisterClick}
                        partnerType={partnerType}
                        onPartnerTypeChange={onPartnerTypeChange}
                        isLoading={isLoading}
                        errorMessage={generalError}
                        fieldErrors={fieldErrors}
                    />
                )}
                {step === Steps.Approve && (
                    <PartnerApprovalPage
                        onSubmit={onPartnerConfirmClick}
                        onResendForm={onResendFormData}
                        onBack={onBackFromApproval}
                        isLoading={isLoading}
                        errorMessage={generalError}
                    />
                )}
                {step === Steps.BankData && (
                    <PartnerBankDetails
                        onSubmit={onBankDetailsSubmit}
                        isLoading={isLoading}
                        errorMessage={generalError}
                        fieldErrors={fieldErrors}
                    />
                )}
                {step === Steps.SignerData && (
                    <PartnerSignerDataPage
                        onSubmit={onSignerDataSubmit}
                        isLoading={isLoading}
                        errorMessage={generalError}
                        fieldErrors={fieldErrors}
                    />
                )}
                {step === Steps.Done && (
                    <PartnerDownloadOffer
                        onSubmit={onSubmitOfferClick}
                        onDownloadError={onDownloadOfferError}
                        isLoading={isLoading}
                        errorMessage={generalError}
                    />
                )}

                {showSelfEmployedDialog ? (
                    <PartnerSelfEmployedDialog
                        onClose={() => setShowSelfEmployedDialog(false)}
                        onRegistered={handleSelfEmployedRegistered}
                    />
                ) : null}
            </Flex>
        </DashboardPage>
    );
});
